import {useState} from 'react';
import {Button, Modal, Text, Spinner, BlockStack} from '@shopify/polaris';
import {useI18n} from '@shopify/react-i18n';

import {slide1, slide2, slide3, slide4, slide5} from './images';
import styles from './OnboardingModal.scss';

interface Props {
  open: boolean;
  onClose(): void;
}

const TOTAL_SLIDES_COUNT = 5;

export function OnboardingModal({open, onClose}: Props) {
  const [i18n] = useI18n();
  const [imagesLoading, setImagesLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(1);
  const images: {
    [key: number]: string;
  } = {
    1: slide1,
    2: slide2,
    3: slide3,
    4: slide4,
    5: slide5,
  };
  const handleOnClose = () => {
    onClose();
    setCurrentSlide(1);
  };
  const handlePreviousSlideClick = () => {
    if (currentSlide > 1) {
      setCurrentSlide(currentSlide - 1);
    }
  };
  const handleNextSlideClick = () => {
    if (currentSlide === TOTAL_SLIDES_COUNT) {
      handleOnClose();
    } else if (currentSlide < TOTAL_SLIDES_COUNT) {
      setCurrentSlide(currentSlide + 1);
    }
  };
  const previousButtonMarkup =
    currentSlide === 1 ? null : (
      <Button onClick={handlePreviousSlideClick}>
        {i18n.translate('OnboardingModal.footer.previous')}
      </Button>
    );

  const header = i18n.translate(
    `OnboardingModal.slides.${currentSlide}.header`,
  );
  const title = i18n.translate(`OnboardingModal.slides.${currentSlide}.title`);
  const description = i18n.translate(
    `OnboardingModal.slides.${currentSlide}.description`,
  );
  const slideMarkUp = (
    <div className={styles.SlideContentContainer}>
      <BlockStack inlineAlign="center" gap="400">
        {imagesLoading ? <Spinner /> : null}
        <Text variant="bodySm" tone="success" as="h2" fontWeight="medium">
          <div className={styles.SlideHeader}>{header}</div>
        </Text>
        <Text variant="headingLg" as="p" fontWeight="regular">
          {title}
        </Text>
        <p className={styles.SlideDescription}>{description}</p>
      </BlockStack>
    </div>
  );
  const slideImageMarkup = (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <img
      className={styles.SlideImage}
      src={images[currentSlide]}
      alt={i18n.translate('OnboardingModal.slides.altText')}
      onLoad={() => setImagesLoading(false)}
    />
  );
  const footer = (
    <div className={styles.FooterContainer}>
      <div className={styles.PreviousButton}>{previousButtonMarkup}</div>
      <p className={styles.PageCount}>
        {i18n.translate('OnboardingModal.footer.pageCount', {
          currentPageCount: currentSlide,
          maxPageCount: TOTAL_SLIDES_COUNT,
        })}
      </p>
      <div className={styles.NextButton}>
        <Button variant="primary" onClick={handleNextSlideClick}>
          {currentSlide === TOTAL_SLIDES_COUNT
            ? i18n.translate('OnboardingModal.footer.close')
            : i18n.translate('OnboardingModal.footer.next')}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      title={i18n.translate('OnboardingModal.title')}
      open={open}
      onClose={handleOnClose}
    >
      {slideImageMarkup}
      {slideMarkUp}
      {footer}
    </Modal>
  );
}
